import React from 'react';
import axios from 'axios';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './contexts/AuthContext';
import { ConfirmProvider } from './components/ConfirmDialog';
import { ThemeProvider } from './contexts/ThemeContext';
import { InsufficientFundsProvider } from './components/InsufficientFundsDialog';
import RedirectGate from './components/RedirectGate';
import AdminRedirects from './pages/AdminRedirects';
import { insufficientFundsInfo } from './utils/apiError';
import { WebSocketProvider } from './contexts/WebSocketContext';
import ErrorBoundary from './components/ErrorBoundary';

// Global axios interceptor — any HTTP 402 with the structured "insufficient
// wallet funds" detail dispatches a window event that the
// InsufficientFundsProvider listens for, opening the top-up popup. This
// means every component using axios gets the popup automatically, with no
// per-call wiring.
axios.interceptors.response.use(
  (r) => r,
  (err) => {
    const info = insufficientFundsInfo(err);
    if (info) {
      try {
        window.dispatchEvent(new CustomEvent('eventism:insufficient-funds', { detail: info }));
      } catch (_) { /* ignore */ }
    }
    return Promise.reject(err);
  },
);

// Components
import Navbar from './components/Navbar';
import ScannerReturnPill from './components/ScannerReturnPill';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';

// Pages
import LandingPage from './pages/LandingPage';
import HomePage from './pages/HomePage';
import TicketingHomePage from './pages/TicketingHomePage';
import OctoberChallengePage from './pages/OctoberChallengePage';
import AdminOctoberChallenge from './pages/AdminOctoberChallenge';
import ManagedServicesPage from './pages/ManagedServicesPage';
import AdminManagedServices from './pages/AdminManagedServices';
import EditorialGuidePage from './pages/EditorialGuidePage';
import AdminEditorialGuides from './pages/AdminEditorialGuides';
import AdminEditorProfile from './pages/AdminEditorProfile';
import EditorialGuidesIndex from './pages/EditorialGuidesIndex';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AuthCallback from './pages/AuthCallback';
import PricingCalculator from './pages/PricingCalculator';
import EventPlanningHub from './pages/EventPlanningHub';
import ClientDashboard from './pages/ClientDashboard';
import AdminDashboard from './pages/AdminDashboard';
import AdminTransactions from './pages/AdminTransactions';
import VendorDashboard from './pages/VendorDashboard';
import VendorPublicProfile from './pages/VendorPublicProfile';
import Marketplace from './pages/Marketplace';
import EventismStaffing from './pages/EventismStaffing';
import EventDetail from './pages/EventDetail';
import PaymentSuccess from './pages/PaymentSuccess';
import B2BDashboard from './pages/B2BDashboard';
import ProposalGenerator from './pages/ProposalGenerator';
import AdminProductManager from './pages/AdminProductManager';
import AdminVenues from './pages/AdminVenues';
import AdminEventismBot from './pages/AdminEventismBot';
import AdminEditorialBot from './pages/AdminEditorialBot';
import EditorialSubmissionPage from './pages/EditorialSubmissionPage';
import MarketExpansionDashboard from './pages/MarketExpansionDashboard';
import AdminIndexing from './pages/AdminIndexing';
import OrganizationApply from './pages/OrganizationApply';
import OrganizationDashboard from './pages/OrganizationDashboard';
import DeveloperDashboard from './pages/DeveloperDashboard';
import OrganizationsDirectory from './pages/OrganizationsDirectory';
import OrganizationProfile from './pages/OrganizationProfile';
import FoodTrucksLanding from './pages/FoodTrucksLanding';
import FoodTruckDetail from './pages/FoodTruckDetail';
import SellTickets from './pages/SellTickets';
import FindEvents from './pages/FindEvents';
import OrganizationMarketing from './pages/OrganizationMarketing';
import CheckInScanner from './pages/CheckInScanner';
import TicketConfirmation from './pages/TicketConfirmation';
import MyTickets from './pages/MyTickets';
import SMSInbox from './pages/SMSInbox';
import QuoteLandingPage from './pages/QuoteLandingPage';
import CategoryListingPage from './pages/CategoryListingPage';
import ProductDetailPage from './pages/ProductDetailPage';
import B2BPricingPage from './pages/B2BPricingPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import ContactPage from './pages/ContactPage';
import HowItWorksPage from './pages/HowItWorksPage';
import AboutPage from './pages/AboutPage';
import HireUsPage from './pages/HireUsPage';
import AdminEventManager from './pages/AdminEventManager';
import EventMarketing from './pages/EventMarketing';
import AdminVenueProposals from './pages/AdminVenueProposals';
import VenueProposalPage from './pages/VenueProposalPage';
import StaffingJobDetail from './pages/StaffingJobDetail';
import MyStaffServices from './pages/MyStaffServices';
import PartnersLanding from './pages/PartnersLanding';
import RentalManager from './pages/RentalManager';
import EventPageBuilder from './pages/EventPageBuilder';
import EventDashboard from './pages/EventDashboard';
import OrderUpgradePage from './pages/OrderUpgradePage';
import PerformerClaimPage from './pages/PerformerClaimPage';
import PerformerProfilePage from './pages/PerformerProfilePage';
import SeriesDirectory from './pages/SeriesDirectory';
import SeriesLandingPage from './pages/SeriesLandingPage';
import AdminBrands from './pages/AdminBrands';
import AdminBrandDetail from './pages/AdminBrandDetail';
import MyMemberships from './pages/MyMemberships';
import SurveyPage from './pages/SurveyPage';
import PromoPartnersDirectory from './pages/PromoPartnersDirectory';
import PromoterSignupPage from './pages/PromoterSignupPage';
import HostGuestListPage from './pages/HostGuestListPage';
import GuestListClaimPage from './pages/GuestListClaimPage';
import WaitlistStatusPage from './pages/WaitlistStatusPage';
import ScanJoinPage from './pages/ScanJoinPage';
import PaySetupPage from './pages/PaySetupPage';
import MoneyPage from './pages/MoneyPage';
import PayoutHistoryPage from './pages/PayoutHistoryPage';
import PartnerPortalPage from './pages/PartnerPortalPage';
import VenueManagePage from './pages/VenueManagePage';
import CityHubPage from './pages/CityHubPage';
import VendorBookingRespondPage from './pages/VendorBookingRespondPage';

// Old supplier URLs (/denver/food/catering/...) now live under /suppliers
function LegacySupplierRedirect() {
  const location = useLocation();
  return <Navigate to={`/suppliers${location.pathname}`} replace />;
}
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import AdminPlatformFinancials from './pages/AdminPlatformFinancials';
import AdminPartnerApplications from './pages/AdminPartnerApplications';
import DoorSalePage from './pages/DoorSalePage';
import ReportsPage from './pages/ReportsPage';
import SharedReportPage from './pages/SharedReportPage';
import EventRedirectPage from './pages/EventRedirectPage';
import StreetTeamDashboard from './pages/StreetTeamDashboard';
import PartnerLandingPage from './pages/PartnerLandingPage';
import PromoterProfilePage from './pages/PromoterProfilePage';
import TicketHolderCRM from './pages/TicketHolderCRM';
import EmbedTicketWidget from './pages/EmbedTicketWidget';
import ServicesShowcase from './pages/ServicesShowcase';
import CityLandingPage from './pages/CityLandingPage';
import PromoCodeAdmin from './pages/PromoCodeAdmin';
import AdminPromoCreditOrder from './pages/AdminPromoCreditOrder';
import AdminStreetTeamWallets from './pages/AdminStreetTeamWallets';
import OrganizerMessages from './pages/OrganizerMessages';
import SiteDirectory from './pages/SiteDirectory';
import AdCreator from './pages/AdCreator';
import EventsListingPage from './pages/EventsListingPage';
import EventDetailPage from './pages/EventDetailPage';
import ExperienceDirectory from './pages/ExperienceDirectory';
import ReservationDirectory from './pages/ReservationDirectory';
import ReviewLandingPage from './pages/ReviewLandingPage';
import BookingSuccessPage from './pages/BookingSuccessPage';
import EmbedBookingPage from './pages/EmbedBookingPage';
import SponsorSignupPage from './pages/SponsorSignupPage';
import SponsorsPage from './pages/SponsorsPage';
import DealsPage from './pages/DealsPage';
import SponsorThanksPage from './pages/SponsorThanksPage';
import SwagBagPage from './pages/SwagBagPage';
import DigitalWallet from './pages/DigitalWallet';
import EventTagsPage from './pages/EventTagsPage';
import EventVenuesPage from './pages/EventVenuesPage';
import CityVenuesIndexPage from './pages/CityVenuesIndexPage';
import WineLandingPage from './pages/WineLandingPage';
import ComedyLandingPage from './pages/ComedyLandingPage';
import BrunchLandingPage from './pages/BrunchLandingPage';
import HolidayLandingPage from './pages/HolidayLandingPage';
import NyeLandingPage from './pages/NyeLandingPage';
import HalloweenLandingPage from './pages/HalloweenLandingPage';
import MyBookingsPage from './pages/MyBookingsPage';
import BookingManagePage from './pages/BookingManagePage';
import WorkspaceListPage from './pages/WorkspaceListPage';
import WorkspaceDetailPage from './pages/WorkspaceDetailPage';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import AdminBlogManager from './pages/AdminBlogManager';
import BlogPage from './pages/BlogPage';
import ProposalLandingPage from './pages/ProposalLandingPage';
import PipelineDashboard from './pages/PipelineDashboard';
import CRMDashboard from './pages/CRMDashboard';
import VendorPartnerPage from './pages/VendorPartnerPage';
import PartnersHub from './pages/PartnersHub';
import SalesAgentRecruitPage from './pages/SalesAgentRecruitPage';
import AdminSettingsPage from './pages/AdminSettingsPage';
import VendorOnboardingWizard from './pages/VendorOnboardingWizard';
import SupplierPerformance from './pages/SupplierPerformance';
import SalesAgentDashboard from './pages/SalesAgentDashboard';
import SalesAgentSettings from './pages/SalesAgentSettings';
import SalesOnboardingWizard from './pages/SalesOnboardingWizard';
import AdminSalesAgents from './pages/AdminSalesAgents';
import AdminAIAgents from './pages/AdminAIAgents';
import VenueOwnerOnboarding from './pages/VenueOwnerOnboarding';
import VenueOwnerDashboard from './pages/VenueOwnerDashboard';
import VenueDetailPage from './pages/VenueDetailPage';
import VenuesMarketplace from './pages/VenuesMarketplace';
import BundleCheckoutPage from './pages/BundleCheckoutPage';
import UniversityDashboard from './pages/UniversityDashboard';
import CourseViewer from './pages/CourseViewer';
import SalesApplicationPage from './pages/SalesApplicationPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import UnsubscribePage from './pages/UnsubscribePage';
import EventTransactionManager from './pages/EventTransactionManager';
import PackageLandingPage from './pages/PackageLandingPage';
import NotFoundPage from './pages/NotFoundPage';
import LiveInvoicePage from './pages/LiveInvoicePage';
import AdminMarketingManager from './pages/AdminMarketingManager';
import ExecutiveCommandCenter from './pages/ExecutiveCommandCenter';
import MarketingPageRenderer from './pages/MarketingPageRenderer';
import AdminTeamManagement from './pages/AdminTeamManagement';
import TeamPortal from './pages/TeamPortal';
import TeamCheckIn from './pages/TeamCheckIn';
import AcceptInvitePage from './pages/AcceptInvitePage';
import AdminPartnerInvitations from './pages/AdminPartnerInvitations';
import AdminPartnerInvitationEditor from './pages/AdminPartnerInvitationEditor';
import PartnerInvitationPage from './pages/PartnerInvitationPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminStakeholders from './pages/AdminStakeholders';
import StakeholderInviteAccept from './pages/StakeholderInviteAccept';
import StakeholderDashboard from './pages/StakeholderDashboard';
import StakeholderEventDashboard from './pages/StakeholderEventDashboard';
import AdminEventStakeholders from './pages/AdminEventStakeholders';
import AdminBridgePanel from './pages/AdminBridgePanel';
import VendorContractPublic from './pages/VendorContractPublic';
import VendorPortalPublic from './pages/VendorPortalPublic';
import ImpersonationBanner from './components/ImpersonationBanner';
import SetPasswordPage from './pages/SetPasswordPage';

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  
  // Track page views
  React.useEffect(() => {
    try {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/pageview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: location.pathname, referrer: document.referrer }),
      }).catch(() => {});
    } catch (e) { /* fire-and-forget */ }
  }, [location.pathname]);

  // Remove Emergent badge
  React.useEffect(() => {
    const removeBadge = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) badge.remove();
    };
    removeBadge();
    const observer = new MutationObserver(removeBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // Handle OAuth callback - check for session_id in hash
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Don't show navbar/footer on auth pages
  const isAuthPage = ['/login', '/register', '/auth/callback'].includes(location.pathname);
  const isLandingPage = location.pathname === '/';
  const isStandaloneMarketing = location.pathname.startsWith('/go/');
  const isPartnerPage = location.pathname.startsWith('/partners') || location.pathname.startsWith('/careers/');
  const isEmbedWidget = location.pathname.startsWith('/embed/');
  const isWalletPage = location.pathname.startsWith('/wallet/');
  const isVendorPublic = location.pathname.startsWith('/contract/') || location.pathname.startsWith('/vendor-portal/');

  return (
    <div className="min-h-screen flex flex-col">
      <ScrollToTop />
      <RedirectGate />
      <ImpersonationBanner />
      <ScannerReturnPill />
      {!isAuthPage && !isStandaloneMarketing && !isPartnerPage && !isEmbedWidget && !isWalletPage && !isVendorPublic && <Navbar />}
      
      <main className="flex-1">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<TicketingHomePage />} />
          <Route path="/discover" element={<HomePage />} />
          <Route path="/october-challenge" element={<OctoberChallengePage />} />
          <Route path="/admin/october" element={<AdminOctoberChallenge />} />
          <Route path="/managed" element={<ManagedServicesPage />} />
          <Route path="/admin/managed" element={<AdminManagedServices />} />
          <Route path="/legacy-home" element={<LandingPage />} />
          {/* Programmatic editorial engine — /{city}/guides/{slug} */}
          <Route path="/guides" element={<EditorialGuidesIndex />} />
          <Route path="/:city/guides" element={<EditorialGuidesIndex />} />
          <Route path="/:city/guides/:slug" element={<EditorialGuidePage />} />
          <Route path="/admin/guides" element={<AdminEditorialGuides />} />
          <Route path="/admin/editor-profile" element={<AdminEditorProfile />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/set-password" element={<SetPasswordPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/partner-hub" element={<PartnerPortalPage />} />
          <Route path="/venue-manage/:city/:venueSlug" element={<VenueManagePage />} />
          <Route path="/vendor-booking/:token" element={<VendorBookingRespondPage />} />
          <Route path="/admin/platform-financials" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminPlatformFinancials />
            </ProtectedRoute>
          } />
          <Route path="/admin/partner-applications" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminPartnerApplications />
            </ProtectedRoute>
          } />
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          <Route path="/admin/events/:eventId/transactions" element={<EventTransactionManager />} />
          {/* Series / Brands public pages */}
          <Route path="/series" element={<SeriesDirectory />} />
          <Route path="/series/:slug" element={<SeriesLandingPage />} />
          <Route path="/series/:slug/welcome" element={<SeriesLandingPage />} />
          <Route path="/survey/:token" element={<SurveyPage />} />
          <Route path="/my/memberships" element={
            <ProtectedRoute>
              <MyMemberships />
            </ProtectedRoute>
          } />
          <Route path="/admin/brands" element={
            <ProtectedRoute allowedRoles={['admin', 'organization']}>
              <AdminBrands />
            </ProtectedRoute>
          } />
          <Route path="/admin/brands/:brandId" element={
            <ProtectedRoute allowedRoles={['admin', 'organization']}>
              <AdminBrandDetail />
            </ProtectedRoute>
          } />
          <Route path="/plan-event" element={<EventPlanningHub />} />
          <Route path="/calculator" element={<EventPlanningHub />} />
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/vendors/:vendorId" element={<VendorPublicProfile />} />
          <Route path="/staffing" element={<EventismStaffing />} />
          <Route path="/shifts" element={<EventismStaffing />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<EventPlanningHub />} />
          <Route path="/quote/:quoteId" element={<QuoteLandingPage />} />
          <Route path="/invoice/:quoteId" element={<LiveInvoicePage />} />
          
          {/* Marketing Pages */}
          <Route path="/go/:slug" element={<MarketingPageRenderer />} />
          
          {/* Static Pages */}
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/hire-us" element={<HireUsPage />} />
          
          {/* B2B Routes */}
          <Route path="/b2b/pricing" element={<B2BPricingPage />} />
          <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
          <Route path="/subscription/cancel" element={<B2BPricingPage />} />
          
          {/* Eventism Events Routes (Public) */}
          <Route path="/events" element={<EventsListingPage />} />
          <Route path="/services" element={<ServicesShowcase />} />
          <Route path="/suppliers/:city" element={<CityLandingPage />} />
          <Route path="/las-vegas" element={<Navigate to="/suppliers/las-vegas" replace />} />
          <Route path="/las_vegas" element={<Navigate to="/suppliers/las-vegas" replace />} />
          <Route path="/los-angeles" element={<Navigate to="/suppliers/los-angeles" replace />} />
          <Route path="/los_angeles" element={<Navigate to="/suppliers/los-angeles" replace />} />
          <Route path="/charlotte" element={<Navigate to="/suppliers/charlotte" replace />} />
          <Route path="/embed/tickets/:eventId" element={<EmbedTicketWidget />} />
          <Route path="/events/:city" element={<FindEvents />} />
          <Route path="/wine" element={<WineLandingPage />} />
          <Route path="/wine/:city" element={<WineLandingPage />} />
          <Route path="/comedy" element={<ComedyLandingPage />} />
          <Route path="/comedy/:city" element={<ComedyLandingPage />} />
          <Route path="/brunch" element={<BrunchLandingPage />} />
          <Route path="/brunch/:city" element={<BrunchLandingPage />} />
          <Route path="/holiday" element={<HolidayLandingPage />} />
          <Route path="/holiday/:city" element={<HolidayLandingPage />} />
          <Route path="/nye" element={<NyeLandingPage />} />
          <Route path="/nye/:city" element={<NyeLandingPage />} />
          <Route path="/halloween" element={<HalloweenLandingPage />} />
          <Route path="/halloween/:city" element={<HalloweenLandingPage />} />
          <Route path="/events/:city/tags/:tag" element={<FindEvents />} />
          {/* New canonical venue URLs — /:city/venues[/:slug]. The old
              /events/:city/venues* routes below stay as aliases so
              production backlinks and SEO don't break. */}
          <Route path="/:city/venues" element={<CityVenuesIndexPage />} />
          <Route path="/:city/venues/:venueSlug" element={<EventVenuesPage />} />
          <Route path="/events/:city/venues" element={<CityVenuesIndexPage />} />
          <Route path="/events/:city/venues/:venueSlug" element={<EventVenuesPage />} />
          <Route path="/events/:city/:slug" element={<EventDetailPage />} />
          {/* ===== Experiences & Reservations (kind-specific URL silos) =====
              Each kind gets its own canonical URL prefix so Google can
              rank them in distinct intents (tours vs ticketed events vs
              restaurant reservations). The detail route reuses
              EventDetailPage which auto-redirects to the correct prefix
              if the user lands on the wrong one. */}
          <Route path="/ex" element={<ExperienceDirectory />} />
          <Route path="/ex/:city" element={<ExperienceDirectory />} />
          <Route path="/ex/:city/:slug" element={<EventDetailPage />} />
          <Route path="/res" element={<ReservationDirectory />} />
          <Route path="/res/:city" element={<ReservationDirectory />} />
          <Route path="/res/:city/:slug" element={<EventDetailPage />} />
          {/* One-tap review landing — links from the post-visit email */}
          <Route path="/review/:token" element={<ReviewLandingPage />} />
          {/* Post-Stripe-Checkout landing page (polls /api/payments/status) */}
          <Route path="/booking/success" element={<BookingSuccessPage />} />
          <Route path="/booking/cancel" element={<BookingSuccessPage />} />
          {/* Embeddable booking widgets — iframe-friendly, no chrome.
              Partners drop /widget.js on their site and we render here. */}
          <Route path="/embed/ex/:eventId" element={<EmbedBookingPage kind="experience" />} />
          <Route path="/embed/res/:eventId" element={<EmbedBookingPage kind="reservation" />} />
          
          {/* Sponsorship + Swag Bag */}
          <Route path="/sponsor/:eventId" element={<SponsorSignupPage />} />
          <Route path="/sponsors" element={<SponsorsPage />} />
          <Route path="/deals" element={<DealsPage />} />
          <Route path="/sponsor/:eventId/thanks" element={<SponsorThanksPage />} />
          <Route path="/swag-bag/:eventId" element={<SwagBagPage />} />
          <Route path="/wallet/:orderId" element={<DigitalWallet />} />
          
          {/* Public Proposal Landing Page - MUST be before SEO catch-all routes */}
          <Route path="/proposal/:proposalSlug" element={<ProposalLandingPage />} />
          
          {/* Public Blog */}
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPage />} />
          
          {/* Public Packages */}
          <Route path="/packages/:slug" element={<PackageLandingPage />} />
          
          {/* Public Venues */}
          <Route path="/venues" element={<VenuesMarketplace />} />
          <Route path="/venues/:state" element={<VenuesMarketplace />} />
          <Route path="/venues/:state/:city" element={<VenuesMarketplace />} />
          <Route path="/venues/:state/:city/:category" element={<VenuesMarketplace />} />
          <Route path="/venues/:state/:city/:category/:venueSlug" element={<VenueDetailPage />} />
          <Route path="/bundle-checkout" element={<BundleCheckoutPage />} />

          {/* Vendor Partner Pages */}
          <Route path="/partners" element={<PartnersHub />} />
          <Route path="/partners/:category" element={<VendorPartnerPage />} />
          
          {/* Sales Agent Recruitment */}
          <Route path="/careers/sales-agent" element={<SalesAgentRecruitPage />} />
          
          {/* Protected Routes - Client */}
          <Route path="/dashboard" element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientDashboard />
            </ProtectedRoute>
          } />
          <Route path="/my-events" element={
            <ProtectedRoute>
              <MyBookingsPage />
            </ProtectedRoute>
          } />
          <Route path="/my-events/:bookingId/manage" element={
            <ProtectedRoute>
              <BookingManagePage />
            </ProtectedRoute>
          } />
          
          {/* Protected Routes - Admin */}
          <Route path="/admin" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/dashboard" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/transactions" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminTransactions />
            </ProtectedRoute>
          } />
          <Route path="/admin/proposals/new" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ProposalGenerator />
            </ProtectedRoute>
          } />
          <Route path="/admin/proposals/new/:leadId" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ProposalGenerator />
            </ProtectedRoute>
          } />
          <Route path="/admin/proposals/:proposalId" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ProposalGenerator />
            </ProtectedRoute>
          } />
          <Route path="/admin/products" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminProductManager />
            </ProtectedRoute>
          } />
          <Route path="/admin/venues" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminVenues />
            </ProtectedRoute>
          } />
          <Route path="/admin/eventism-bot" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminEventismBot />
            </ProtectedRoute>
          } />
          <Route path="/admin/editorial-bot" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminEditorialBot />
            </ProtectedRoute>
          } />
          <Route path="/contribute/:token" element={<EditorialSubmissionPage />} />
          <Route path="/admin/markets" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <MarketExpansionDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/indexing" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminIndexing />
            </ProtectedRoute>
          } />
          <Route path="/admin/checkin/:eventId" element={
            <ProtectedRoute allowedRoles={['admin', 'organization']} allowTeamMembers>
              <CheckInScanner />
            </ProtectedRoute>
          } />
          <Route path="/tickets/confirmation" element={<TicketConfirmation />} />
          <Route path="/order/:orderId/upgrade" element={<OrderUpgradePage />} />
          <Route path="/performers/claim/:token" element={<PerformerClaimPage />} />
          <Route path="/performers/:ptype/:slug" element={<PerformerProfilePage />} />
          <Route path="/my-tickets" element={
            <ProtectedRoute allowedRoles={['admin', 'client', 'vendor', 'sales_agent']}>
              <MyTickets />
            </ProtectedRoute>
          } />
          <Route path="/admin/sms-inbox" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <SMSInbox />
            </ProtectedRoute>
          } />
          <Route path="/admin/marketing" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminMarketingManager />
            </ProtectedRoute>
          } />
          <Route path="/admin/events" element={
            <ProtectedRoute allowedRoles={['admin', 'organization']}>
              <OrganizationDashboard />
            </ProtectedRoute>
          } />
          {/* Legacy AdminEventManager kept for the edit-event modal flow
              via `?edit=<id>` deep-link. Both admins and orgs land here only
              when explicitly editing details from the new dashboard. */}
          <Route path="/admin/events/legacy" element={
            <ProtectedRoute allowedRoles={['admin', 'organization']}>
              <AdminEventManager />
            </ProtectedRoute>
          } />
          <Route path="/event/:eventId/manage" element={
            <ProtectedRoute>
              <EventDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/events/:eventId/marketing" element={
            <ProtectedRoute allowedRoles={['admin', 'organization']}>
              <EventMarketing />
            </ProtectedRoute>
          } />
          <Route path="/admin/events/:eventId/page-builder" element={
            <ProtectedRoute allowedRoles={['admin', 'organization']}>
              <EventPageBuilder />
            </ProtectedRoute>
          } />
          <Route path="/admin/events/:eventId/venue-proposals" element={
            <ProtectedRoute allowedRoles={['admin', 'organization']}>
              <AdminVenueProposals />
            </ProtectedRoute>
          } />
          <Route path="/venue-proposal/:token" element={<VenueProposalPage />} />
          <Route path="/staffing/jobs/:jobId" element={<StaffingJobDetail />} />
          <Route path="/partners/join" element={<PartnersLanding />} />
          <Route path="/organizations/apply" element={<OrganizationApply />} />
          <Route path="/organizations" element={<OrganizationsDirectory />} />
          <Route path="/organizations/:slug" element={<OrganizationProfile />} />
          <Route path="/food-trucks" element={<FoodTrucksLanding />} />
          <Route path="/food-trucks/:slug" element={<FoodTruckDetail />} />
          <Route path="/:city/food-trucks" element={<FoodTrucksLanding />} />
          <Route path="/sell-tickets" element={<SellTickets />} />
          <Route path="/find-events" element={<FindEvents />} />
          <Route path="/organization/dashboard" element={
            <ProtectedRoute allowedRoles={['organization', 'admin']}>
              <OrganizationDashboard />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/api-keys" element={
            <ProtectedRoute allowedRoles={['organization', 'admin']}>
              <DeveloperDashboard />
            </ProtectedRoute>
          } />
          <Route path="/staffing/my-services" element={
            <ProtectedRoute>
              <MyStaffServices />
            </ProtectedRoute>
          } />
          <Route path="/promo-partners" element={<PromoPartnersDirectory />} />
          <Route path="/promoter-signup" element={<PromoterSignupPage />} />
          <Route path="/guest-list/host/:token" element={<HostGuestListPage />} />
          <Route path="/gl/:token" element={<GuestListClaimPage />} />
          <Route path="/waitlist/:token" element={<WaitlistStatusPage />} />
          <Route path="/scan-join/:linkId" element={<ScanJoinPage />} />
          <Route path="/pay-setup/:token" element={<PaySetupPage />} />
          <Route path="/money" element={<ProtectedRoute><MoneyPage /></ProtectedRoute>} />
          <Route path="/payout-history" element={<ProtectedRoute><PayoutHistoryPage /></ProtectedRoute>} />
          <Route path="/door/:eventId" element={<DoorSalePage />} />
          <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
          <Route path="/reports/shared/:token" element={<SharedReportPage />} />
          <Route path="/e/:eventId" element={<EventRedirectPage />} />
          <Route path="/dashboard/street-team" element={
            <ProtectedRoute>
              <StreetTeamDashboard />
            </ProtectedRoute>
          } />
          <Route path="/p/:code/event/:city/:slug" element={<PartnerLandingPage />} />
          <Route path="/promo/:handle" element={<PromoterProfilePage />} />
          <Route path="/admin/ticket-holders" element={
            <ProtectedRoute allowedRoles={['admin', 'organization']}>
              <TicketHolderCRM />
            </ProtectedRoute>
          } />
          <Route path="/admin/redirects" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminRedirects />
            </ProtectedRoute>
          } />
          <Route path="/admin/promo-codes" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <PromoCodeAdmin />
            </ProtectedRoute>
          } />
          <Route path="/admin/promo-partners/credit-order" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminPromoCreditOrder />
            </ProtectedRoute>
          } />
          <Route path="/admin/street-team-wallets" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminStreetTeamWallets />
            </ProtectedRoute>
          } />
          <Route path="/organization/messages" element={
            <ProtectedRoute allowedRoles={['organization', 'admin']}>
              <OrganizerMessages />
            </ProtectedRoute>
          } />
          <Route path="/admin/rentals" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <RentalManager />
            </ProtectedRoute>
          } />
          <Route path="/admin/site-directory" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <SiteDirectory />
            </ProtectedRoute>
          } />
          <Route path="/admin/ad-creator" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdCreator />
            </ProtectedRoute>
          } />
          <Route path="/admin/workspaces" element={
            <ProtectedRoute allowedRoles={['admin', 'staff']}>
              <WorkspaceListPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/workspaces/:workspaceId" element={
            <ProtectedRoute allowedRoles={['admin', 'staff']}>
              <WorkspaceDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/analytics" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AnalyticsDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/blog" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminBlogManager />
            </ProtectedRoute>
          } />
          <Route path="/admin/pipeline" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <PipelineDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/crm" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <CRMDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/supplier-performance" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <SupplierPerformance />
            </ProtectedRoute>
          } />
          <Route path="/admin/sales-agents" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminSalesAgents />
            </ProtectedRoute>
          } />
          <Route path="/admin/ai-agents" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminAIAgents />
            </ProtectedRoute>
          } />
          <Route path="/admin/team" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminTeamManagement />
            </ProtectedRoute>
          } />

          {/* Team Portal — for invited teammates (any authed user) */}
          <Route path="/team" element={
            <ProtectedRoute>
              <TeamPortal />
            </ProtectedRoute>
          } />
          <Route path="/team/checkin" element={
            <ProtectedRoute>
              <TeamCheckIn />
            </ProtectedRoute>
          } />
          <Route path="/team/invite/:token" element={<AcceptInvitePage />} />

          {/* Partner Invitations */}
          <Route path="/admin/partner-invitations" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminPartnerInvitations />
            </ProtectedRoute>
          } />
          <Route path="/admin/partner-invitations/:invitationId" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminPartnerInvitationEditor />
            </ProtectedRoute>
          } />
          <Route path="/partner-invitation/:token" element={<PartnerInvitationPage />} />

          {/* Admin User Directory + Impersonation */}
          <Route path="/admin/users" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminUsersPage />
            </ProtectedRoute>
          } />

          {/* Stakeholders ("NFT Tickets") */}
          <Route path="/admin/stakeholders" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminStakeholders />
            </ProtectedRoute>
          } />
          <Route path="/stakeholder/invite/:token" element={<StakeholderInviteAccept />} />
          <Route path="/stakeholder/invite/:token/thanks" element={<StakeholderInviteAccept />} />
          <Route path="/contract/:token" element={<VendorContractPublic />} />
          <Route path="/vendor-portal/:token" element={<VendorPortalPublic />} />
          <Route path="/stakeholder/dashboard" element={
            <ProtectedRoute allowedRoles={['admin', 'stakeholder']}>
              <StakeholderDashboard />
            </ProtectedRoute>
          } />
          <Route path="/stakeholder/event/:eventId" element={
            <ProtectedRoute allowedRoles={['admin', 'stakeholder']}>
              <StakeholderEventDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/events/:eventId/stakeholders" element={
            <ProtectedRoute allowedRoles={['admin', 'organization']}>
              <AdminEventStakeholders />
            </ProtectedRoute>
          } />
          <Route path="/admin/bridge" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminBridgePanel />
            </ProtectedRoute>
          } />
          
          {/* Protected Routes - Sales Agent */}
          <Route path="/sales/apply" element={
            <ProtectedRoute allowedRoles={['sales_agent']}>
              <SalesApplicationPage />
            </ProtectedRoute>
          } />
          <Route path="/sales/onboarding" element={
            <ProtectedRoute allowedRoles={['sales_agent']}>
              <SalesOnboardingWizard />
            </ProtectedRoute>
          } />
          <Route path="/sales" element={
            <ProtectedRoute allowedRoles={['sales_agent']}>
              <SalesAgentDashboard />
            </ProtectedRoute>
          } />
          <Route path="/sales/crm" element={
            <ProtectedRoute allowedRoles={['sales_agent']}>
              <CRMDashboard />
            </ProtectedRoute>
          } />
          <Route path="/sales/proposals/new" element={
            <ProtectedRoute allowedRoles={['sales_agent']}>
              <ProposalGenerator />
            </ProtectedRoute>
          } />
          <Route path="/sales/proposals/new/:leadId" element={
            <ProtectedRoute allowedRoles={['sales_agent']}>
              <ProposalGenerator />
            </ProtectedRoute>
          } />
          <Route path="/sales/proposals/:proposalId" element={
            <ProtectedRoute allowedRoles={['sales_agent']}>
              <ProposalGenerator />
            </ProtectedRoute>
          } />
          <Route path="/sales/settings" element={
            <ProtectedRoute allowedRoles={['sales_agent']}>
              <SalesAgentSettings />
            </ProtectedRoute>
          } />
          
          {/* University - Public Training Hub + Protected Dashboard */}
          <Route path="/training" element={<UniversityDashboard />} />
          <Route path="/university" element={
            <ProtectedRoute>
              <UniversityDashboard />
            </ProtectedRoute>
          } />
          <Route path="/university/course/:courseId" element={
            <ProtectedRoute>
              <CourseViewer />
            </ProtectedRoute>
          } />
          <Route path="/university/executive" element={
            <ProtectedRoute>
              <ExecutiveCommandCenter />
            </ProtectedRoute>
          } />
          
          {/* Public Blog - These routes already exist earlier */}
          
          {/* Protected Routes - Vendor */}
          <Route path="/vendor" element={
            <ProtectedRoute allowedRoles={['vendor', 'admin']}>
              <VendorDashboard />
            </ProtectedRoute>
          } />
          <Route path="/vendor/onboarding" element={
            <ProtectedRoute>
              <VendorOnboardingWizard />
            </ProtectedRoute>
          } />
          
          {/* Protected Routes - Venue Owner */}
          <Route path="/venue-dashboard" element={
            <ProtectedRoute allowedRoles={['venue_owner']}>
              <VenueOwnerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/venue-onboarding" element={
            <ProtectedRoute allowedRoles={['venue_owner']}>
              <VenueOwnerOnboarding />
            </ProtectedRoute>
          } />
          
          {/* Protected Routes - B2B */}
          <Route path="/b2b" element={
            <ProtectedRoute allowedRoles={['b2b']}>
              <B2BDashboard />
            </ProtectedRoute>
          } />
          
          {/* Settings - accessible to all authenticated users */}
          <Route path="/settings" element={
            <ProtectedRoute>
              <SalesAgentSettings />
            </ProtectedRoute>
          } />
          <Route path="/admin/settings" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminSettingsPage />
            </ProtectedRoute>
          } />
          
          {/* SEO-friendly service listing routes - MUST be last as they're catch-all */}
          <Route path="/suppliers/:city/:category" element={<CategoryListingPage />} />
          <Route path="/suppliers/:city/:category/:subCategory" element={<CategoryListingPage />} />
          <Route path="/suppliers/:city/:category/:subCategory/:slug" element={<ProductDetailPage />} />
          <Route path="/:city" element={<CityHubPage />} />
          <Route path="/:city/:category" element={<CityHubPage />} />
          <Route path="/:city/:category/:subCategory" element={<LegacySupplierRedirect />} />
          <Route path="/:city/:category/:subCategory/:slug" element={<LegacySupplierRedirect />} />
          
          {/* Catch-all */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>

      {!isAuthPage && !isLandingPage && !isStandaloneMarketing && !isPartnerPage && !isEmbedWidget && !isWalletPage && !isVendorPublic && <Footer />}
      {isLandingPage && <Footer />}
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <WebSocketProvider>
            <BrowserRouter>
              <ConfirmProvider>
                <InsufficientFundsProvider>
                  <ErrorBoundary>
                    <AppRouter />
                  </ErrorBoundary>
                  <Toaster position="top-right" richColors />
                </InsufficientFundsProvider>
              </ConfirmProvider>
            </BrowserRouter>
          </WebSocketProvider>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
